import * as React from 'react';
import { Component } from 'react';
import i18n from 'i18n';
import { Button, ButtonGroup, Tab, Tabs } from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import { BlazeTableColDef } from 'components/blazeTable/BlazeTable.types';
import { HttpCallParams } from 'types/rest/analyticsTypes';
import BlazeTable from 'components/blazeTable/BlazeTable';
import Row from 'react-bootstrap/Row';
import OutdatedRequest from 'views/Analytics/AnalyticsMockService/Inspection/RequestDetail/OutdatedRequest';
import FullPageLoader from 'components/loaders/FullPageLoader';
import _ from 'lodash';
import { DetailLevelTabType, DetailTabType } from '../../../../store/analytics/ui/analyticsUiTypes';
import { HttpViewType } from './HttpViewUtils';
import ExpandableBody from './ExpandableBody';

interface HttpCallDetailsStatelessProps {
  tabId: any;
  isLoading: boolean;
  httpRequest: any;
  isOutdated: boolean;
  details: HttpCallParams;
  selectedLevel: DetailLevelTabType;
  selectedTab: DetailTabType;
  onRefresh: (tabId: any) => void;
  viewType: HttpViewType;
  selectHttpCallsDetailTab: (tabName: DetailTabType) => void;
}

/**
 * Inspection detail of HTTP / Webhook call.
 */
class HttpCallDetails extends Component<HttpCallDetailsStatelessProps> {
  tableRef: any = React.createRef<BlazeTable>();

  // Definition of table columns
  colsDefinition: BlazeTableColDef[] = [
    {
      header: i18n.t('analytics:webhook.detail.name'),
      width: 30,
      accessor: 'key',
    },
    {
      header: i18n.t('analytics:webhook.detail.value'),
      width: 70,
      accessor: 'value',
    },
  ];

  getTableOptions = () => ({
    showRowExpander: false,
    columnDef: this.colsDefinition,
    pageable: true,
    selectable: false,
    pageSize: 5,
    minWidth: 950,
    minHeight: 150,
    tableName: 'webhookDetails',
    noResultsText: i18n.t('analytics:webhook.table.noResults'),
    customHeaderClassName: 'flex-table-item-top',
    customTableClassName: 'flex-table-simple flex-table-condensed',
    maxContentHeight: '185px',
  });

  render() {
    const title = this.props.viewType === 'webhook' ? 'Selected Webhook' : 'Selected HTTP Call';
    return (
      <div className="mt-4 pt-4 position-relative" style={{ borderTop: '1px solid #c8d1e0', minHeight: '400px' }}>
        {this.props.isLoading && (
          <div className="blur-background">
            <FullPageLoader />
          </div>
        )}

        <div className={this.props.isLoading ? 'blur' : ''}>
          {/* Header */}
          {this.props.isOutdated === false && (
            <div className="mb-2 mt-2" style={{ minHeight: 30 }}>
              <Row className="mt-3 mb-3">
                <Col lg={10} md={7} sm={6}>
                  <h2 className="mb-0 text-truncate pt-2" title={title + this.props.httpRequest.webHookName}>
                    <span className="ml-3">
                      {title}
                      <span className="text-muted ml-2 mr-2">:</span>
                      {this.props.httpRequest.webHookName}
                    </span>
                  </h2>
                </Col>
                <Col className={'text-right'} lg={2} md={5} sm={6}>
                  <ButtonGroup>
                    <Button
                      variant={this.props.selectedTab === 'defaultTab' ? 'primary' : 'secondary'}
                      onClick={() => {
                        this.props.selectHttpCallsDetailTab('defaultTab');
                      }}
                    >
                      {i18n.t('analytics:txInspection.detail.requestDetails')}
                    </Button>
                    <Button
                      variant={this.props.selectedTab === 'secondTab' ? 'primary' : 'secondary'}
                      onClick={() => this.props.selectHttpCallsDetailTab('secondTab')}
                    >
                      {i18n.t('analytics:txInspection.detail.responseDetails')}
                    </Button>
                  </ButtonGroup>
                </Col>
              </Row>
            </div>
          )}
          {/* Outdated Request Placeholder */}
          {this.props.isOutdated === true && (
            <OutdatedRequest refreshRequestsHandler={() => this.props.onRefresh(this.props.tabId)} />
          )}

          {/* Render body only when details are ready */}
          {!_.isNil(this.props.details) && !_.isNil(this.props.details.header) && this.props.isOutdated === false && (
            <>
              {this.props.selectedTab === 'secondTab' && (
                <div className="mt-2 mb-2">
                  <label className="mb-3 mr-3">{i18n.t('analytics:txInspection.detail.statusCode')}:</label>
                  <span>{this.props.details.resultCode}</span>
                </div>
              )}
              <div className="position-relative">
                <Tabs
                  defaultActiveKey={this.props.selectedLevel}
                  activeKey={this.props.selectedLevel}
                  id="mock-service-analytics-tabs"
                >
                  {this.props.selectedTab === 'defaultTab' && (
                    <Tab
                      eventKey="queryParams"
                      title={'Query Parameters (' + this.props.details.queryParams.length + ')'}
                    >
                      <BlazeTable
                        options={this.getTableOptions()}
                        data={this.props.details.queryParams}
                        onRef={(ref: any) => (this.tableRef = ref)}
                        showPageSizeDropdown={true}
                      />
                    </Tab>
                  )}

                  <Tab eventKey="Header" title={'Headers (' + this.props.details.header.length + ')'}>
                    <BlazeTable
                      options={this.getTableOptions()}
                      data={this.props.details.header}
                      onRef={(ref: any) => (this.tableRef = ref)}
                      showPageSizeDropdown={true}
                    />
                  </Tab>
                  <Tab eventKey="Body" title="Body">
                    <label className="d-inline-block mb-3">
                      {this.props.viewType === 'webhook'
                        ? i18n.t('analytics:webhook.detail.bodyContent')
                        : i18n.t('analytics:httpCalls.detail.bodyContent')}
                    </label>

                    {this.props.details.bodyContent ? (
                      <div className={'position-relative'} style={{ bottom: '22px' }}>
                        <ExpandableBody expandedHeight={1000} height={360}>
                          {this.props.details.bodyContent}
                        </ExpandableBody>
                      </div>
                    ) : (
                      <p>{i18n.t('analytics:webhook.detail.noBodyContentAvailable')}</p>
                    )}
                  </Tab>
                </Tabs>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default HttpCallDetails;
